import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import { 
  Calendar, Users, Bell, MapPin, Music, CheckCircle2, 
  Star, ArrowRight, Building2, TrendingUp, Clock, Shield
} from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/2197bd5f-e421-4534-9309-6e77e73c8080/images/1b5c52ada9c7aa57be2cbf3d44bd7eae9bded4b369e93da8a9e03f935f9d8a1d.png";
const EVENTS_IMG = "https://static.prod-images.emergentagent.com/jobs/2197bd5f-e421-4534-9309-6e77e73c8080/images/e79276ca98cb962061f39e8cbca781c2a1d7bd7f1585d7454eb6f52ee3382fb2.png";

export default function LandingBares() {
  const features = [
    {
      icon: Building2,
      title: "PERFIL COMPLETO",
      description: "Crea un perfil atractivo con fotos, descripción, horarios de apertura y ubicación en Google Maps."
    },
    {
      icon: Calendar,
      title: "GESTIÓN DE EVENTOS",
      description: "Publica conciertos, sesiones de DJ, vermuts y más. Añade fecha, precio, link de entradas y géneros musicales."
    },
    {
      icon: Users,
      title: "CONECTA CON BANDAS",
      description: "Las bandas pueden encontrar tu local y proponerte fechas. Tú decides qué solicitudes aprobar."
    },
    {
      icon: Clock,
      title: "CALENDARIO DE DISPONIBILIDAD",
      description: "Marca las fechas en las que estás disponible para conciertos y define tus condiciones de reserva."
    },
    {
      icon: Bell,
      title: "NOTIFICACIONES",
      description: "Recibe emails cuando una banda te proponga un concierto. Mantente al día sin esfuerzo."
    },
    {
      icon: TrendingUp,
      title: "VISIBILIDAD",
      description: "Tu bar aparece en el mapa de la escena rock local. Los fans te encontrarán fácilmente."
    }
  ];

  const plans = [
    {
      name: "GRATIS",
      price: "0€",
      period: "/mes",
      features: [
        "Perfil del bar completo",
        "Publicar eventos ilimitados",
        "Recibir solicitudes de bandas",
        "Calendario de disponibilidad",
        "Notificaciones por email"
      ],
      cta: "EMPEZAR GRATIS",
      highlighted: false
    },
    {
      name: "PRO",
      price: "50€",
      period: "/mes",
      features: [
        "Todo lo del plan Gratis",
        "2 publicaciones semanales gestionadas",
        "Diseño de cartelería",
        "Posicionamiento destacado",
        "Soporte prioritario"
      ],
      cta: "ACTUALIZAR A PRO",
      highlighted: true
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-primary/20 text-primary font-mono text-sm mb-6">
                PARA BARES Y SALAS
              </span>
              <h1 className="text-4xl lg:text-6xl font-black text-white mb-6 leading-tight">
                TU BAR EN EL MAPA DE LA <span className="text-primary">ESCENA ROCK</span>
              </h1>
              <p className="text-xl text-zinc-400 mb-8 leading-relaxed">
                Gestiona tu local, publica eventos, conecta con bandas y llena tu sala de fans del rock y metal.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register?type=bar">
                  <Button className="bg-primary hover:bg-red-700 text-white font-mono text-lg px-8 py-6" data-testid="bar-register-btn">
                    REGISTRAR MI BAR
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login?type=bar">
                  <Button variant="outline" className="border-zinc-700 text-zinc-300 font-mono text-lg px-8 py-6">
                    YA TENGO CUENTA
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-full" />
              <img 
                src={HERO_IMG} 
                alt="Dashboard de bar en Rockmaps" 
                className="relative rounded-lg border border-zinc-800 shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-[#111]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
              TODO LO QUE NECESITAS
            </h2>
            <p className="text-zinc-500 font-mono max-w-2xl mx-auto">
              Herramientas diseñadas para que tu bar destaque en la escena musical local
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-[#0a0a0a] border border-zinc-800 p-6 hover:border-primary/50 transition-colors group"
                data-testid={`feature-${index}`}
              >
                <feature.icon className="w-10 h-10 text-primary mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshot Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={EVENTS_IMG} 
                alt="Creación de eventos en Rockmaps" 
                className="rounded-lg border border-zinc-800 shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-6">
                PUBLICA EVENTOS EN MINUTOS
              </h2>
              <ul className="space-y-4">
                {[
                  "Añade título, descripción y cartel del evento",
                  "Define fecha, hora y precio de entrada",
                  "Selecciona géneros musicales y etiquetas",
                  "Incluye link de venta de entradas",
                  "Publica o guarda como borrador"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register?type=bar" className="inline-block mt-8">
                <Button className="bg-primary hover:bg-red-700 text-white font-mono">
                  CREAR MI PRIMER EVENTO
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-[#111]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
              PLANES PARA TU BAR
            </h2>
            <p className="text-zinc-500 font-mono">
              Empieza gratis y actualiza cuando quieras
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`p-8 border ${
                  plan.highlighted 
                    ? "bg-primary/10 border-primary" 
                    : "bg-[#0a0a0a] border-zinc-800"
                }`}
                data-testid={`plan-${plan.name.toLowerCase()}`}
              >
                {plan.highlighted && (
                  <span className="inline-block px-3 py-1 bg-primary text-white text-xs font-mono mb-4">
                    RECOMENDADO
                  </span>
                )}
                <h3 className="text-2xl font-black text-white mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-black text-white">{plan.price}</span>
                  <span className="text-zinc-500 font-mono">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/register?type=bar">
                  <Button 
                    className={`w-full font-mono ${
                      plan.highlighted 
                        ? "bg-primary hover:bg-red-700 text-white" 
                        : "bg-zinc-800 hover:bg-zinc-700 text-white"
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Music className="w-16 h-16 text-primary mx-auto mb-6" />
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-6">
            ¿LISTO PARA LLENAR TU BAR DE ROCK?
          </h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
            Únete a la comunidad de bares y salas que ya están conectando con bandas y fans en Rockmaps.
          </p>
          <Link to="/register?type=bar">
            <Button className="bg-primary hover:bg-red-700 text-white font-mono text-lg px-12 py-6">
              REGISTRAR MI BAR GRATIS
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-zinc-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            <span className="font-black text-white">ROCK<span className="text-primary">MAPS</span></span>
          </div>
          <div className="flex gap-6 text-zinc-500 font-mono text-sm">
            <Link to="/para-bandas" className="hover:text-primary">PARA BANDAS</Link>
            <Link to="/events" className="hover:text-primary">EVENTOS</Link>
            <Link to="/bars" className="hover:text-primary">BARES</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
